import {useCallback, useEffect, useMemo, useState} from "react";
import {css, Theme} from "@emotion/react";
import {css as pandaCss} from "@panda/css";
import {useFormikContext} from "formik";

import {Button} from "@pg-design/button-module";
import {Expandable} from "@pg-design/expandable-module";
import {borderRadius, calculateRemSize, flex, flexAbsoluteCenter, m, mb, mt, onDesktop, p, pb, ph, pl} from "@pg-design/helpers-css";
import {ChevronDownIcon, ChevronUpIcon, CloseIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {AdvancedScroll} from "@pg-mono/advanced-scroll-module";
import {OFFER_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {LeadButtonTerms} from "../../../../lead/components/LeadButtonTerms";
import {LeadRodoTerms} from "../../../../lead/components/LeadRodoTerms";
import {LeadVendorRodoTerms} from "../../../../lead/components/LeadVendorRodoTerms";
import {askForPriceleadButtonCopy} from "../../../../lead/constants/lead_button_copy";
import {useLastSeenPropertiesDetails} from "../../../../property/hooks/use_last_seen_properties_details";
import {removeLastSeenPropertiesByOfferIds} from "../../../../property/utils/user_last_seen_offer_properties";
import {LastSeenPropertiesGTMEventAction, lastSeenPropertiesLeadAnalytics} from "../../../tracking/last_seen_properties_lead_tracking";
import {IApplicationFormValues} from "../../../types/IApplicationFormValues";
import {getStorageSentApplicationsList} from "../../../utils/manage_storage_sent_applications";
import {ApplicationOfferSelectionHeader} from "../application_offer/ApplicationOfferSelectionHeader";
import {ApplicationOfferBigBox} from "../application_offer/offer_box/ApplicationOfferBigBox";
import {ApplicationOfferSelectionBox} from "../application_offer/offer_box/ApplicationOfferSelectionBox";
import {ApplicationOfferSentSummaryBox} from "../application_offer/offer_box/ApplicationOfferSentSummaryBox";
import {useApplicationFormContext} from "../ApplicationFormContext";
import {ApplicationOptIns} from "../fields/ApplicationOptIns";
import {YourData} from "../fields/YourData";
import {ApplicationRevealButtonWithWrapper} from "./ApplicationRevealButtonWithWrapper";
import {SubmitButton} from "./SubmitButton";

interface IProps {
    showEmailField: boolean;
    showPhoneField: boolean;
    showNameField: boolean;
    showAllInputFields: () => void;
    showTextAreaOnly: boolean;
    showMktEmailField: boolean;
    showMktSmsField: boolean;
    showRevealPriceButton: boolean;
}

const LAST_SEEN_PROPERTIES_LIMIT = 6;

export const ApplicationRetargetingForm = (props: IProps) => {
    const {offer, property, vendor, hideSubmitButton, onCloseModal, leadIsSubmitting} = useApplicationFormContext();
    const {setFieldValue} = useFormikContext<IApplicationFormValues>();
    const {isMobile} = useUserDevice();
    const sentApplicationIds = useMemo(() => getStorageSentApplicationsList(offer?.region.country).map((sentApplication) => sentApplication.id), [offer]);
    const {
        propertyDetailList,
        propertiesCount,
        isLoading: isPropertiesDetailsLoading
    } = useLastSeenPropertiesDetails({excludeOfferIds: sentApplicationIds, limit: LAST_SEEN_PROPERTIES_LIMIT});
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([]);
    const togglePropertySelection = useCallback((propertyId: number) => {
        setSelectedPropertyIds((prevSelectedPropertyIds) => {
            if (prevSelectedPropertyIds.includes(propertyId)) {
                lastSeenPropertiesLeadAnalytics.gtm.lastSeenPropertiesLeadEvent(LastSeenPropertiesGTMEventAction.UNCHECK);
                return prevSelectedPropertyIds.filter((id) => id !== propertyId);
            }

            lastSeenPropertiesLeadAnalytics.gtm.lastSeenPropertiesLeadEvent(LastSeenPropertiesGTMEventAction.CHECK);
            return [...prevSelectedPropertyIds, propertyId];
        });
    }, []);

    useEffect(() => {
        // remove offers which user already sent application for
        removeLastSeenPropertiesByOfferIds(sentApplicationIds);
    }, [sentApplicationIds.join(",")]);

    useEffect(() => {
        setFieldValue("multiLeadProperties", selectedPropertyIds);
    }, [selectedPropertyIds]);

    useEffect(() => {
        if (!isPropertiesDetailsLoading && propertyDetailList.length > 0) {
            lastSeenPropertiesLeadAnalytics.gtm.lastSeenPropertiesLeadEvent(LastSeenPropertiesGTMEventAction.OFFER_VIEW);
        }
    }, [propertyDetailList, isPropertiesDetailsLoading]);

    // TODO - If pre selected properties are needed, uncomment this code
    // useEffect(() => {
    //     if (propertyDetailList.length > 0) {
    //         setSelectedPropertyIds(propertyDetailList.map((propertyDetail) => propertyDetail.id));
    //     }
    // }, [propertyDetailList]);

    return (
        <div css={wrapperStyle}>
            <div css={headerStyle}>
                <Text variant="headline_4">Zapytaj o ofertę</Text>

                <div css={closeIconWrapperStyle} onClick={() => onCloseModal()}>
                    <CloseIcon size="2.4" wrapperSize="2.4" />
                </div>
            </div>
            <div css={contentStyle}>
                <div className={leftSideWrap}>
                    {!isPropertiesDetailsLoading && propertiesCount === 0 ? (
                        offer && (
                            <ApplicationOfferBigBox
                                offerType={offer.type}
                                offerAddress={offer.address}
                                offerMainImg={offer.main_image.m_img_1500}
                                offerVendorLogo={vendor?.logo.v_log_80x60}
                                rooms={property?.rooms}
                                area={property?.area}
                                offerVendorName={vendor?.name || ""}
                                css={[mb(3), onDesktop(m(0, 3, 3, 3))]}
                            />
                        )
                    ) : (
                        <AdvancedScroll
                            height={650}
                            css={offersColumnStyle}
                            contentClassName={!isMobile ? offersColumnContentPandaStyle : undefined}
                            scrollTopShadowClassName={contentTopShadowPandaStyle}
                            scrollBottomShadowClassName={contentBottomShadowPandaStyle}
                            disableScroll={!!isMobile || propertyDetailList.length < 2}
                        >
                            {!isMobile && propertyDetailList.length >= 2 && (
                                <div css={[applicationOfferSentSummaryBoxMaskStyle]} className={contentTopShadowPandaStyle} />
                            )}
                            {offer && (
                                <ApplicationOfferSentSummaryBox
                                    offerType={offer.type}
                                    offerAddress={offer.address}
                                    offerMainImg={offer.main_image.m_img_375x211}
                                    offerVendorLogo={vendor?.logo.v_log_80x60}
                                    rooms={property?.rooms}
                                    area={property?.area}
                                    offerVendorName={vendor?.name || ""}
                                    css={mb(2)}
                                />
                            )}

                            <Expandable
                                isAlwaysExpanded={!isMobile}
                                isExpandedByDefault={!!isMobile}
                                collapsedHeight={160}
                                customCollapseButton={({isExpanded, toggleCollapse}) => (
                                    <Button
                                        variant="filled_secondary"
                                        size="x-small"
                                        type="button"
                                        iconRight={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                                        onClick={toggleCollapse}
                                        css={[expandableButtonStyle, ph(3)]}
                                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.EXPAND_OFFER_BUTTON_MOBILE}
                                    >
                                        {isExpanded ? "Zwiń oferty" : "Rozwiń oferty"}
                                    </Button>
                                )}
                            >
                                <div css={retargetedOffersWrapperStyle}>
                                    <ApplicationOfferSelectionHeader />

                                    <div css={retargetedOfferListStyle}>
                                        {isPropertiesDetailsLoading ? (
                                            <Loader size="lg" fullWidth />
                                        ) : (
                                            <>
                                                {propertyDetailList.map((propertyDetail) => (
                                                    <ApplicationOfferSelectionBox
                                                        key={propertyDetail.id}
                                                        propertyId={propertyDetail.id}
                                                        offerType={propertyDetail.offer.type}
                                                        offerAddress={propertyDetail.offer.address}
                                                        offerMainImg={propertyDetail.offer.main_image.m_img_375x211}
                                                        rooms={propertyDetail.rooms}
                                                        area={propertyDetail.area}
                                                        offerVendorName={propertyDetail.offer.vendor.name}
                                                        offerVendorLogo={propertyDetail.offer.vendor.logo?.v_log_80x60}
                                                        offerPrivacyPolicyUrl={propertyDetail.offer.configuration.privacy_policy_url}
                                                        offerPrivacyDataProcessorName={propertyDetail.offer.configuration?.personal_data_processor_name}
                                                        offerPersonalDataProcessorUrl={propertyDetail.offer.configuration?.personal_data_processor_url}
                                                        selectedPropertyIds={selectedPropertyIds}
                                                        togglePropertySelection={togglePropertySelection}
                                                    />
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Expandable>
                        </AdvancedScroll>
                    )}
                    {!isMobile && (
                        <div className={desktopLeadTerms}>
                            <LeadRodoTerms />
                            {vendor && (
                                <LeadVendorRodoTerms
                                    vendor={vendor}
                                    buttonText={askForPriceleadButtonCopy}
                                    offerPrivacyPolicyUrl={offer?.configuration.privacy_policy_url}
                                    offerPersonalDataProcessorName={offer?.configuration.personal_data_processor_name}
                                    offerPersonalDataProcessorUrl={offer?.configuration.personal_data_processor_url}
                                />
                            )}
                        </div>
                    )}
                </div>
                <div css={formColumnStyle}>
                    <YourData
                        hideTopLabel
                        showEmailField={props.showEmailField}
                        showPhoneField={props.showPhoneField}
                        showNameField={props.showNameField}
                        onShowAllInputFieldsClick={props.showAllInputFields}
                        css={yourDataStyle}
                    />
                    <ApplicationOptIns showMktEmailField={props.showMktEmailField} showMktSmsField={props.showMktSmsField} css={mb(2)} />
                    <div className={leadSubmit}>
                        {!hideSubmitButton &&
                            (props.showRevealPriceButton ? (
                                <ApplicationRevealButtonWithWrapper leadIsSubmitting={leadIsSubmitting} leadBtnText={askForPriceleadButtonCopy} />
                            ) : (
                                <SubmitButton css={mt(2)} type="submit" variant="filled_primary" disabled={leadIsSubmitting}>
                                    {leadIsSubmitting ? <Loader /> : <>Wyślij</>}
                                </SubmitButton>
                            ))}
                    </div>
                    <div className={leadTerms}>
                        <LeadButtonTerms buttonText={askForPriceleadButtonCopy} />
                        {isMobile && (
                            <>
                                <LeadRodoTerms />
                                {vendor && (
                                    <LeadVendorRodoTerms
                                        vendor={vendor}
                                        buttonText={askForPriceleadButtonCopy}
                                        offerPrivacyPolicyUrl={offer?.configuration.privacy_policy_url}
                                        offerPersonalDataProcessorName={offer?.configuration.personal_data_processor_name}
                                        offerPersonalDataProcessorUrl={offer?.configuration.personal_data_processor_url}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const wrapperStyle = (theme: Theme) => css`
    ${p(1.5)};
    background-color: ${theme.colors.gray[100]};

    ${onDesktop(css`
        width: 90rem;
        ${p(3, 3, 0, 0)};
    `)};
`;

const headerStyle = css`
    ${mb(3)};
    display: flex;
    justify-content: space-between;

    ${onDesktop(css`
        ${pl(3)};
    `)};
`;

const closeIconWrapperStyle = css`
    width: 2.4rem;
    height: 2.4rem;
    ${flexAbsoluteCenter};
    cursor: pointer;
`;

const contentStyle = css`
    ${onDesktop(css`
        ${flex()};
        flex-direction: row;
        gap: ${calculateRemSize(3)};
    `)};
`;

const offersColumnStyle = css`
    ${onDesktop(css`
        flex: 1 1 auto;
        margin-top: -${calculateRemSize(1)};
    `)};
`;

const applicationOfferSentSummaryBoxMaskStyle = css`
    position: absolute;
    inset: 0 0 auto 0;
    height: 10px;
    z-index: 3;
`;

const expandableButtonStyle = css`
    transform: translateY(-100%);
`;

const retargetedOffersWrapperStyle = css`
    overflow: hidden;

    ${onDesktop(css`
        background-color: #fff;
        ${borderRadius(2)};
    `)};
`;

const retargetedOfferListStyle = css`
    ${pb(2)};
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${calculateRemSize(1.5)};

    ${onDesktop(css`
        ${p(2, 1)};
    `)};
`;

const formColumnStyle = css`
    ${onDesktop(css`
        flex: 0 0 34rem;
    `)};
`;

export const yourDataStyle = css`
    ${m(0, 0, 1.5, 0)};

    ${onDesktop(css`
        ${m(0, 0, 3, 0)};
    `)};
`;

// Panda CSS styles
const offersColumnContentPandaStyle = pandaCss({
    md: {
        pt: 1,
        pr: 2,
        pb: 2,
        pl: 3
    }
});

const contentTopShadowPandaStyle = pandaCss({
    background: "linear-gradient(rgba(249, 249, 249, 1), rgba(249, 249, 249, 0))"
});

const contentBottomShadowPandaStyle = pandaCss({
    background: "linear-gradient(rgba(249, 249, 249, 0), rgba(249, 249, 249, 1))"
});

const leadSubmit = pandaCss({
    mb: 1,
    md: {
        mb: 2
    }
});

const leadTerms = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pb: 2
});

const desktopLeadTerms = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 2,
    flex: "0 0 auto"
});

const leftSideWrap = pandaCss({
    display: "flex",
    flexDirection: "column"
});
import {useCallback, useEffect, useMemo, useState} from "react";
import {css, Theme} from "@emotion/react";
import {css as pandaCss} from "@panda/css";
import {useFormikContext} from "formik";

import {Button} from "@pg-design/button-module";
import {Expandable} from "@pg-design/expandable-module";
import {borderRadius, calculateRemSize, flex, flexAbsoluteCenter, m, mb, mt, onDesktop, p, pb, ph, pl} from "@pg-design/helpers-css";
import {ChevronDownIcon, ChevronUpIcon, CloseIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {AdvancedScroll} from "@pg-mono/advanced-scroll-module";
import {OFFER_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {LeadButtonTerms} from "../../../../lead/components/LeadButtonTerms";
import {LeadRodoTerms} from "../../../../lead/components/LeadRodoTerms";
import {LeadVendorRodoTerms} from "../../../../lead/components/LeadVendorRodoTerms";
import {askForPriceleadButtonCopy} from "../../../../lead/constants/lead_button_copy";
import {useLastSeenPropertiesDetails} from "../../../../property/hooks/use_last_seen_properties_details";
import {removeLastSeenPropertiesByOfferIds} from "../../../../property/utils/user_last_seen_offer_properties";
import {LastSeenPropertiesGTMEventAction, lastSeenPropertiesLeadAnalytics} from "../../../tracking/last_seen_properties_lead_tracking";
import {IApplicationFormValues} from "../../../types/IApplicationFormValues";
import {getStorageSentApplicationsList} from "../../../utils/manage_storage_sent_applications";
import {ApplicationOfferSelectionHeader} from "../application_offer/ApplicationOfferSelectionHeader";
import {ApplicationOfferBigBox} from "../application_offer/offer_box/ApplicationOfferBigBox";
import {ApplicationOfferSelectionBox} from "../application_offer/offer_box/ApplicationOfferSelectionBox";
import {ApplicationOfferSentSummaryBox} from "../application_offer/offer_box/ApplicationOfferSentSummaryBox";
import {useApplicationFormContext} from "../ApplicationFormContext";
import {ApplicationOptIns} from "../fields/ApplicationOptIns";
import {YourData} from "../fields/YourData";
import {ApplicationRevealButtonWithWrapper} from "./ApplicationRevealButtonWithWrapper";
import {SubmitButton} from "./SubmitButton";

interface IProps {
    showEmailField: boolean;
    showPhoneField: boolean;
    showNameField: boolean;
    showAllInputFields: () => void;
    showTextAreaOnly: boolean;
    showMktEmailField: boolean;
    showMktSmsField: boolean;
    showRevealPriceButton: boolean;
}

const LAST_SEEN_PROPERTIES_LIMIT = 6;

export const ApplicationRetargetingForm = (props: IProps) => {
    const {offer, property, vendor, hideSubmitButton, onCloseModal, leadIsSubmitting} = useApplicationFormContext();
    const {setFieldValue} = useFormikContext<IApplicationFormValues>();
    const {isMobile} = useUserDevice();
    const sentApplicationIds = useMemo(() => getStorageSentApplicationsList(offer?.region.country).map((sentApplication) => sentApplication.id), [offer]);
    const {
        propertyDetailList,
        propertiesCount,
        isLoading: isPropertiesDetailsLoading
    } = useLastSeenPropertiesDetails({excludeOfferIds: sentApplicationIds, limit: LAST_SEEN_PROPERTIES_LIMIT});
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([]);
    const togglePropertySelection = useCallback((propertyId: number) => {
        setSelectedPropertyIds((prevSelectedPropertyIds) => {
            if (prevSelectedPropertyIds.includes(propertyId)) {
                lastSeenPropertiesLeadAnalytics.gtm.lastSeenPropertiesLeadEvent(LastSeenPropertiesGTMEventAction.UNCHECK);
                return prevSelectedPropertyIds.filter((id) => id !== propertyId);
            }

            lastSeenPropertiesLeadAnalytics.gtm.lastSeenPropertiesLeadEvent(LastSeenPropertiesGTMEventAction.CHECK);
            return [...prevSelectedPropertyIds, propertyId];
        });
    }, []);

    useEffect(() => {
        // remove offers which user already sent application for
        removeLastSeenPropertiesByOfferIds(sentApplicationIds);
    }, [sentApplicationIds.join(",")]);

    useEffect(() => {
        setFieldValue("multiLeadProperties", selectedPropertyIds);
    }, [selectedPropertyIds]);

    useEffect(() => {
        if (!isPropertiesDetailsLoading && propertyDetailList.length > 0) {
            lastSeenPropertiesLeadAnalytics.gtm.lastSeenPropertiesLeadEvent(LastSeenPropertiesGTMEventAction.OFFER_VIEW);
        }
    }, [propertyDetailList, isPropertiesDetailsLoading]);

    // TODO - If pre selected properties are needed, uncomment this code
    // useEffect(() => {
    //     if (propertyDetailList.length > 0) {
    //         setSelectedPropertyIds(propertyDetailList.map((propertyDetail) => propertyDetail.id));
    //     }
    // }, [propertyDetailList]);

    return (
        <div css={wrapperStyle}>
            <div css={headerStyle}>
                <Text variant="headline_4">Zapytaj o ofertę</Text>

                <div css={closeIconWrapperStyle} onClick={() => onCloseModal()}>
                    <CloseIcon size="2.4" wrapperSize="2.4" />
                </div>
            </div>
            <div css={contentStyle}>
                <div className={leftSideWrap}>
                    {!isPropertiesDetailsLoading && propertiesCount === 0 ? (
                        offer && (
                            <ApplicationOfferBigBox
                                offerType={offer.type}
                                offerAddress={offer.address}
                                offerMainImg={offer.main_image.m_img_1500}
                                offerVendorLogo={vendor?.logo.v_log_80x60}
                                rooms={property?.rooms}
                                area={property?.area}
                                offerVendorName={vendor?.name || ""}
                                css={[mb(3), onDesktop(m(0, 3, 3, 3))]}
                            />
                        )
                    ) : (
                        <AdvancedScroll
                            height={650}
                            css={offersColumnStyle}
                            contentClassName={!isMobile ? offersColumnContentPandaStyle : undefined}
                            scrollTopShadowClassName={contentTopShadowPandaStyle}
                            scrollBottomShadowClassName={contentBottomShadowPandaStyle}
                            disableScroll={!!isMobile || propertyDetailList.length < 2}
                        >
                            {!isMobile && propertyDetailList.length >= 2 && (
                                <div css={[applicationOfferSentSummaryBoxMaskStyle]} className={contentTopShadowPandaStyle} />
                            )}
                            {offer && (
                                <ApplicationOfferSentSummaryBox
                                    offerType={offer.type}
                                    offerAddress={offer.address}
                                    offerMainImg={offer.main_image.m_img_375x211}
                                    offerVendorLogo={vendor?.logo.v_log_80x60}
                                    rooms={property?.rooms}
                                    area={property?.area}
                                    offerVendorName={vendor?.name || ""}
                                    css={mb(2)}
                                />
                            )}

                            <Expandable
                                isAlwaysExpanded={!isMobile}
                                isExpandedByDefault={!!isMobile}
                                collapsedHeight={160}
                                customCollapseButton={({isExpanded, toggleCollapse}) => (
                                    <Button
                                        variant="filled_secondary"
                                        size="x-small"
                                        type="button"
                                        iconRight={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                                        onClick={toggleCollapse}
                                        css={[expandableButtonStyle, ph(3)]}
                                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.EXPAND_OFFER_BUTTON_MOBILE}
                                    >
                                        {isExpanded ? "Zwiń oferty" : "Rozwiń oferty"}
                                    </Button>
                                )}
                            >
                                <div css={retargetedOffersWrapperStyle}>
                                    <ApplicationOfferSelectionHeader />

                                    <div css={retargetedOfferListStyle}>
                                        {isPropertiesDetailsLoading ? (
                                            <Loader size="lg" fullWidth />
                                        ) : (
                                            <>
                                                {propertyDetailList.map((propertyDetail) => (
                                                    <ApplicationOfferSelectionBox
                                                        key={propertyDetail.id}
                                                        propertyId={propertyDetail.id}
                                                        offerType={propertyDetail.offer.type}
                                                        offerAddress={propertyDetail.offer.address}
                                                        offerMainImg={propertyDetail.offer.main_image.m_img_375x211}
                                                        rooms={propertyDetail.rooms}
                                                        area={propertyDetail.area}
                                                        offerVendorName={propertyDetail.offer.vendor.name}
                                                        offerVendorLogo={propertyDetail.offer.vendor.logo?.v_log_80x60}
                                                        offerPrivacyPolicyUrl={propertyDetail.offer.configuration.privacy_policy_url}
                                                        offerPrivacyDataProcessorName={propertyDetail.offer.configuration?.personal_data_processor_name}
                                                        offerPersonalDataProcessorUrl={propertyDetail.offer.configuration?.personal_data_processor_url}
                                                        selectedPropertyIds={selectedPropertyIds}
                                                        togglePropertySelection={togglePropertySelection}
                                                    />
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Expandable>
                        </AdvancedScroll>
                    )}
                    {!isMobile && (
                        <div className={desktopLeadTerms}>
                            <LeadRodoTerms />
                            {vendor && (
                                <LeadVendorRodoTerms
                                    vendor={vendor}
                                    buttonText={askForPriceleadButtonCopy}
                                    offerPrivacyPolicyUrl={offer?.configuration.privacy_policy_url}
                                    offerPersonalDataProcessorName={offer?.configuration.personal_data_processor_name}
                                    offerPersonalDataProcessorUrl={offer?.configuration.personal_data_processor_url}
                                />
                            )}
                        </div>
                    )}
                </div>
                <div css={formColumnStyle}>
                    <YourData
                        hideTopLabel
                        showEmailField={props.showEmailField}
                        showPhoneField={props.showPhoneField}
                        showNameField={props.showNameField}
                        onShowAllInputFieldsClick={props.showAllInputFields}
                        css={yourDataStyle}
                    />
                    <ApplicationOptIns showMktEmailField={props.showMktEmailField} showMktSmsField={props.showMktSmsField} css={mb(2)} />
                    <div className={leadSubmit}>
                        {!hideSubmitButton &&
                            (props.showRevealPriceButton ? (
                                <ApplicationRevealButtonWithWrapper leadIsSubmitting={leadIsSubmitting} leadBtnText={askForPriceleadButtonCopy} />
                            ) : (
                                <SubmitButton css={mt(2)} type="submit" variant="filled_primary" disabled={leadIsSubmitting}>
                                    {leadIsSubmitting ? <Loader /> : <>Wyślij</>}
                                </SubmitButton>
                            ))}
                    </div>
                    <div className={leadTerms}>
                        <LeadButtonTerms buttonText={askForPriceleadButtonCopy} />
                        {isMobile && (
                            <>
                                <LeadRodoTerms />
                                {vendor && (
                                    <LeadVendorRodoTerms
                                        vendor={vendor}
                                        buttonText={askForPriceleadButtonCopy}
                                        offerPrivacyPolicyUrl={offer?.configuration.privacy_policy_url}
                                        offerPersonalDataProcessorName={offer?.configuration.personal_data_processor_name}
                                        offerPersonalDataProcessorUrl={offer?.configuration.personal_data_processor_url}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const wrapperStyle = (theme: Theme) => css`
    ${p(1.5)};
    background-color: ${theme.colors.gray[100]};

    ${onDesktop(css`
        width: 90rem;
        ${p(3, 3, 0, 0)};
    `)};
`;

const headerStyle = css`
    ${mb(3)};
    display: flex;
    justify-content: space-between;

    ${onDesktop(css`
        ${pl(3)};
    `)};
`;

const closeIconWrapperStyle = css`
    width: 2.4rem;
    height: 2.4rem;
    ${flexAbsoluteCenter};
    cursor: pointer;
`;

const contentStyle = css`
    ${onDesktop(css`
        ${flex()};
        flex-direction: row;
        gap: ${calculateRemSize(3)};
    `)};
`;

const offersColumnStyle = css`
    ${onDesktop(css`
        flex: 1 1 auto;
        margin-top: -${calculateRemSize(1)};
    `)};
`;

const applicationOfferSentSummaryBoxMaskStyle = css`
    position: absolute;
    inset: 0 0 auto 0;
    height: 10px;
    z-index: 3;
`;

const expandableButtonStyle = css`
    transform: translateY(-100%);
`;

const retargetedOffersWrapperStyle = css`
    overflow: hidden;

    ${onDesktop(css`
        background-color: #fff;
        ${borderRadius(2)};
    `)};
`;

const retargetedOfferListStyle = css`
    ${pb(2)};
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${calculateRemSize(1.5)};

    ${onDesktop(css`
        ${p(2, 1)};
    `)};
`;

const formColumnStyle = css`
    ${onDesktop(css`
        flex: 0 0 34rem;
    `)};
`;

export const yourDataStyle = css`
    ${m(0, 0, 1.5, 0)};

    ${onDesktop(css`
        ${m(0, 0, 3, 0)};
    `)};
`;

// Panda CSS styles
const offersColumnContentPandaStyle = pandaCss({
    md: {
        pt: 1,
        pr: 2,
        pb: 2,
        pl: 3
    }
});

const contentTopShadowPandaStyle = pandaCss({
    background: "linear-gradient(rgba(249, 249, 249, 1), rgba(249, 249, 249, 0))"
});

const contentBottomShadowPandaStyle = pandaCss({
    background: "linear-gradient(rgba(249, 249, 249, 0), rgba(249, 249, 249, 1))"
});

const leadSubmit = pandaCss({
    mb: 1,
    md: {
        mb: 2
    }
});

const leadTerms = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pb: 2
});

const desktopLeadTerms = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 2,
    flex: "0 0 auto"
});

const leftSideWrap = pandaCss({
    display: "flex",
    flexDirection: "column"
});
